import { useCallback, useEffect, useState } from "react";
import { Formik } from "formik";
import { Row, Col } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import Select from "../../../components/Select";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Input.css";
import "../../../styles/Issue.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import { formats, hasPermission, modules } from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import { createTripCostEstimationSchema } from "./createTripCostEstimation.schema";

const formatTripCostEstimationDisplayRoute = (value = "") =>
  String(value || "")
    .replace(/\s*(?:->|>)\s*/g, " -> ")
    .replace(/\s*->\s*/g, " \u2192 ")
    .trim();

const formatTripCostEstimationTableRoute = (value = "") =>
  String(value || "")
    .replace(/\s*(?:->|\u2192)\s*/g, " > ")
    .trim();

const toPositiveNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const resolveTripDurationMultiplier = (value) => {
  const parsedValue = Number(value);

  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }

  return String(value || "").trim() ? 1 : 0;
};

const buildTripCostEstimationMetrics = (values = {}) => {
  const distance = toPositiveNumber(values.distance);
  const fuelPricePerLiter = toPositiveNumber(values.fuelPricePerLiter);
  const vehicleFuelEfficiency = toPositiveNumber(values.vehicleFuelEfficiency);
  const foodAllowancePerDay = toPositiveNumber(values.foodAllowancePerDay);
  const tripDurationMultiplier = resolveTripDurationMultiplier(values.tripDuration);
  const maintenanceCostPerKm = toPositiveNumber(values.maintenanceCostPerKm);
  const estimatedTollCost = toPositiveNumber(values.estimatedTollCost);
  const estimatedRevenue = toPositiveNumber(values.estimatedRevenue);

  const estimatedFuelCost =
    vehicleFuelEfficiency > 0
      ? Math.round((distance / vehicleFuelEfficiency) * fuelPricePerLiter)
      : 0;
  const estimatedFoodCost = Math.round(foodAllowancePerDay * tripDurationMultiplier);
  const estimatedMaintenanceCost = Math.round(maintenanceCostPerKm * distance);
  const estimatedCost = Math.round(
    estimatedFuelCost +
      estimatedFoodCost +
      estimatedMaintenanceCost +
      estimatedTollCost
  );
  const projectedProfit = Math.round(estimatedRevenue - estimatedCost);

  return {
    estimatedFuelCost,
    estimatedFoodCost,
    estimatedMaintenanceCost,
    estimatedCost,
    estimatedRevenue,
    projectedProfit,
  };
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const pickOption = (options = [], value = "") =>
  (Array.isArray(options) ? options : []).find(
    (option) => String(option?.value ?? "") === String(value ?? "")
  ) || null;

const prependMissingOption = (options = [], nextOption = null) => {
  if (!nextOption?.value) return options;

  if (
    (options || []).some(
      (option) => String(option?.value ?? "") === String(nextOption?.value ?? "")
    )
  ) {
    return options;
  }

  return [nextOption, ...options];
};

const normalizeTripDropdownOption = (option = {}) => {
  const resolvedValue = option?.value ?? option?.id ?? "";
  const resolvedLabel =
    option?.label ??
    option?.trip_id ??
    option?.name ??
    (resolvedValue !== "" ? String(resolvedValue) : "");

  return {
    ...option,
    value: resolvedValue,
    label: resolvedLabel,
  };
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const resolveUploadedFilePath = (fileEntry) => {
  if (typeof fileEntry === "string") return String(fileEntry).trim();
  if (!fileEntry || typeof fileEntry !== "object") return "";

  return String(
    fileEntry?.fileName ||
      fileEntry?.file_path ||
      fileEntry?.file_name ||
      fileEntry?.path ||
      fileEntry?.url ||
      fileEntry?.file ||
      fileEntry?.image ||
      (typeof fileEntry?.data === "string" ? fileEntry.data : "") ||
      fileEntry?.data?.fileName ||
      fileEntry?.data?.file_path ||
      fileEntry?.data?.file_name ||
      fileEntry?.data?.path ||
      fileEntry?.data?.url ||
      fileEntry?.data?.file ||
      fileEntry?.data?.image ||
      ""
  ).trim();
};

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const buildRouteMeta = (detail = {}) => {
  const startLocation = String(detail?.start_trip?.location || "").trim();
  const endLocation = String(detail?.end_trip?.location || "").trim();
  const fallbackRoute = String(detail?.route || "").trim();
  const routeLabel =
    [startLocation, endLocation].filter(Boolean).join(" \u2192 ") ||
    formatTripCostEstimationDisplayRoute(fallbackRoute);
  const route =
    [startLocation, endLocation].filter(Boolean).join(" > ") ||
    formatTripCostEstimationTableRoute(fallbackRoute);

  return {
    routeId: route || routeLabel || "",
    routeLabel,
    route,
  };
};

const resetFetchedTripFieldTouches = (setFieldTouched) => {
  [
    "vehicleId",
    "driverId",
    "driverName",
    "routeId",
    "routeLabel",
    "distance",
    "tripDuration",
  ].forEach((fieldName) => {
    setFieldTouched(fieldName, false, false);
  });
};

const hasFetchedTripFieldValue = (fieldName, fieldValue) => {
  if (fieldName === "distance") {
    return Number(fieldValue) > 0;
  }

  return String(fieldValue ?? "").trim() !== "";
};

const syncFetchedTripFieldValidation = (
  setFieldTouched,
  validateForm,
  nextValues = {}
) => {
  [
    "vehicleId",
    "driverId",
    "routeId",
    "distance",
    "tripDuration",
  ].forEach((fieldName) => {
    setFieldTouched(
      fieldName,
      !hasFetchedTripFieldValue(fieldName, nextValues?.[fieldName]),
      false
    );
  });

  validateForm(nextValues);
};

const clearFetchedTripFields = (setFieldValue) => {
  setFieldValue("vehicleId", "", false);
  setFieldValue("driverId", "", false);
  setFieldValue("driverName", "", false);
  setFieldValue("driverImage", "", false);
  setFieldValue("routeId", "", false);
  setFieldValue("routeLabel", "", false);
  setFieldValue("route", "", false);
  setFieldValue("distance", "", false);
  setFieldValue("tripDuration", "", false);
};

const buildCurrentOptions = (values = {}, tripDropdownOptions = []) => ({
  tripOptions: prependMissingOption(tripDropdownOptions, {
    value: values.tripId,
    label:
      values.tripLabel ||
      values.tripCode ||
      String(values.tripId || "").trim() ||
      "Trip",
  }),
  vehicleOptions: prependMissingOption([], {
    value: values.vehicleId,
    label: values.vehicleId || "Vehicle",
  }),
  driverOptions: prependMissingOption([], {
    value: values.driverId,
    label: values.driverName || values.driverId || "Driver",
    showIcon: true,
    image: values.driverImage || "",
  }),
  routeOptions: prependMissingOption([], {
    value: values.routeId,
    label: values.routeLabel || values.route || values.routeId || "Route",
    tableLabel: values.route || values.routeLabel || values.routeId || "Route",
  }),
});

const buildTripCostEstimationDetailValues = (item = {}, existingRecord = {}) => {
  const distance = toNumber(item?.distance, toNumber(existingRecord?.distance));
  const maintenanceCost = toNumber(
    item?.maintenance_cost,
    toNumber(existingRecord?.estimatedMaintenanceCost)
  );
  const maintenanceCostPerKm =
    distance > 0 ? Number((maintenanceCost / distance).toFixed(6)) : 0;

  return {
    id: item?.id ?? existingRecord?.id ?? "",
    estimationId: String(
      item?.trip_cost_estimate_id || existingRecord?.estimationId || ""
    ).trim(),
    tripId:
      item?.trip_id !== undefined && item?.trip_id !== null && item?.trip_id !== ""
        ? Number(item.trip_id)
        : existingRecord?.tripId || "",
    tripRecordId:
      item?.trip_id !== undefined && item?.trip_id !== null && item?.trip_id !== ""
        ? Number(item.trip_id)
        : existingRecord?.tripRecordId || "",
    tripLabel: String(
      item?.trip?.trip_id ||
        existingRecord?.tripLabel ||
        existingRecord?.tripCode ||
        ""
    ).trim(),
    tripCode: String(
      item?.trip?.trip_id ||
        existingRecord?.tripCode ||
        existingRecord?.tripLabel ||
        ""
    ).trim(),
    vehicleId: String(
      item?.trip?.vehicle?.registration_number || existingRecord?.vehicleId || ""
    ).trim(),
    driverId: String(existingRecord?.driverId || "").trim(),
    driverName: String(existingRecord?.driverName || "").trim(),
    driverImage: String(existingRecord?.driverImage || "").trim(),
    routeId: String(existingRecord?.routeId || "").trim(),
    routeLabel: String(existingRecord?.routeLabel || "").trim(),
    route: String(existingRecord?.route || "").trim(),
    distance,
    tripDuration: String(item?.duration || existingRecord?.tripDuration || "").trim(),
    fuelPricePerLiter: toNumber(
      item?.fuel_price_per_ltr,
      toNumber(existingRecord?.fuelPricePerLiter)
    ),
    vehicleFuelEfficiency: toNumber(
      item?.fuel_efficiency_per_ltr,
      toNumber(existingRecord?.vehicleFuelEfficiency)
    ),
    foodAllowancePerDay: toNumber(
      item?.driver_allowance,
      toNumber(existingRecord?.foodAllowancePerDay)
    ),
    maintenanceCostPerKm,
    estimatedTollCost: toNumber(
      item?.toll_cost,
      toNumber(existingRecord?.estimatedTollCost)
    ),
    estimatedRevenue: toNumber(
      item?.estimated_total_revenue,
      toNumber(existingRecord?.estimatedRevenue)
    ),
    attachments: Array.isArray(item?.files)
      ? item.files
      : Array.isArray(existingRecord?.attachments)
        ? existingRecord.attachments
        : [],
    notes: String(item?.note || existingRecord?.notes || "").trim(),
    createdDate: String(item?.created_at || existingRecord?.createdDate || "")
      .trim()
      .slice(0, 10),
    createdBy: String(item?.creator?.username || existingRecord?.createdBy || "").trim(),
    createdByImage: String(
      item?.creator?.image || existingRecord?.createdByImage || ""
    ).trim(),
    historyItems: Array.isArray(existingRecord?.historyItems)
      ? existingRecord.historyItems
      : [],
  };
};

const mergeTripEstimateDetailIntoRecord = (record = {}, detail = {}) => {
  const routeMeta = buildRouteMeta(detail);

  return {
    ...record,
    tripLabel: String(
      detail?.trip_id || record?.tripLabel || record?.tripCode || ""
    ).trim(),
    tripCode: String(
      detail?.trip_id || record?.tripCode || record?.tripLabel || ""
    ).trim(),
    vehicleId: String(
      detail?.vehicle?.registration_number ||
        detail?.vehicle?.name ||
        detail?.vehicle?.id ||
        record?.vehicleId ||
        ""
    ).trim(),
    driverId: String(
      detail?.primary_driver?.id ||
        detail?.primary_driver?.user_id ||
        record?.driverId ||
        ""
    ).trim(),
    driverName: String(
      detail?.primary_driver?.username ||
        detail?.primary_driver?.name ||
        record?.driverName ||
        ""
    ).trim(),
    driverImage: String(
      detail?.primary_driver?.image || record?.driverImage || ""
    ).trim(),
    routeId: String(routeMeta.routeId || record?.routeId || "").trim(),
    routeLabel: String(routeMeta.routeLabel || record?.routeLabel || "").trim(),
    route: String(routeMeta.route || record?.route || "").trim(),
    distance: detail?.distance ?? record?.distance ?? "",
    tripDuration: String(detail?.duration || record?.tripDuration || "").trim(),
  };
};

const normalizeComparableValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? Number(value) : 0;
  }

  return String(value ?? "").trim();
};

const areComparableValuesEqual = (left, right) => {
  const normalizedLeft = normalizeComparableValue(left);
  const normalizedRight = normalizeComparableValue(right);

  if (Array.isArray(normalizedLeft) || Array.isArray(normalizedRight)) {
    const leftArray = Array.isArray(normalizedLeft) ? [...normalizedLeft].sort() : [];
    const rightArray = Array.isArray(normalizedRight) ? [...normalizedRight].sort() : [];
    return JSON.stringify(leftArray) === JSON.stringify(rightArray);
  }

  return normalizedLeft === normalizedRight;
};

const buildUpdatePayloadFromValues = (recordId, values = {}) => {
  const metrics = buildTripCostEstimationMetrics(values);

  return {
    trip_cost_estimate_id: Number(recordId),
    trip_id: Number(values.tripRecordId || values.tripId || 0),
    distance: Number(values.distance || 0),
    duration: String(values.tripDuration || "").trim(),
    fuel_price_per_ltr: Number(values.fuelPricePerLiter || 0),
    fuel_efficiency_per_ltr: Number(values.vehicleFuelEfficiency || 0),
    fuel_cost: metrics.estimatedFuelCost,
    maintenance_cost: metrics.estimatedMaintenanceCost,
    toll_cost: Number(values.estimatedTollCost || 0),
    driver_allowance: metrics.estimatedFoodCost,
    estimated_total_cost: metrics.estimatedCost,
    estimated_total_revenue: Number(values.estimatedRevenue || 0),
    estimated_total_profit: metrics.projectedProfit,
    note: String(values.notes || "").trim(),
    files: normalizeToArray(values.attachments)
      .map((file) => resolveUploadedFilePath(file))
      .filter(Boolean),
  };
};

const buildUpdatedKeys = (currentPayload = {}, initialPayload = {}) => {
  const keysToTrack = [
    "trip_id",
    "distance",
    "duration",
    "fuel_price_per_ltr",
    "fuel_efficiency_per_ltr",
    "fuel_cost",
    "maintenance_cost",
    "toll_cost",
    "driver_allowance",
    "estimated_total_cost",
    "estimated_total_revenue",
    "estimated_total_profit",
    "note",
    "files",
  ];

  return keysToTrack.filter(
    (key) => !areComparableValuesEqual(currentPayload?.[key], initialPayload?.[key])
  );
};

const loadTripCostEstimationRecord = async (estimationId, existingRecord = {}) => {
  const response = await apiService.getTripCostEstimateDetail(estimationId);
  const detailItem = response?.data || {};
  let nextRecord = buildTripCostEstimationDetailValues(detailItem, existingRecord);

  const tripRecordId = Number(nextRecord?.tripRecordId || 0);
  if (tripRecordId > 0) {
    try {
      const tripResponse = await apiService.getTripEstimateDetail(tripRecordId);
      nextRecord = mergeTripEstimateDetailIntoRecord(
        nextRecord,
        tripResponse?.data || {}
      );
    } catch (error) {
      // Keep base detail values if trip estimate enrichment is unavailable.
    }
  }

  return nextRecord;
};

const TripCostEstimationDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [estimationRecord, setEstimationRecord] = useState(null);
  const [initialUpdatePayload, setInitialUpdatePayload] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [commentValue, setCommentValue] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [tripDropdownOptions, setTripDropdownOptions] = useState([]);
  const [tripDropdownLoading, setTripDropdownLoading] = useState(false);
  const [tripDetailLoading, setTripDetailLoading] = useState(false);
  const locationEstimationRecord = location.state?.estimation || null;
  const canUpdateEstimation = hasPermission({
    permissionSlug: FinancePermission.Update,
    menuSlug: FinanceSubModules.TripCostEstimation,
    moduleSlug: AppMainModules.finance,
  });
  const estimationHistoryId = Number(estimationRecord?.id || id || 0);
  const canUseEstimationHistoryApi =
    Number.isFinite(estimationHistoryId) && estimationHistoryId > 0;

  useEffect(() => {
    let isMounted = true;

    const fetchTripDropdown = async () => {
      setTripDropdownLoading(true);

      try {
        const response = await apiService.getTripListsDropdown();
        const nextOptions = Array.isArray(response?.data)
          ? response.data.map(normalizeTripDropdownOption)
          : [];

        if (!isMounted) return;
        setTripDropdownOptions(nextOptions);
      } catch (error) {
        if (!isMounted) return;
        setTripDropdownOptions([]);
        errorNotify(error || "Failed to fetch trips dropdown");
      } finally {
        if (isMounted) {
          setTripDropdownLoading(false);
        }
      }
    };

    fetchTripDropdown();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const existingRecord = locationEstimationRecord || {};

    setHistoryItems(existingRecord?.historyItems || []);
    setCommentValue("");
    setPageLoading(true);

    const fetchDetail = async () => {
      try {
        const nextRecord = await loadTripCostEstimationRecord(id, existingRecord);
        if (!isMounted) return;

        setEstimationRecord(nextRecord);
        setHistoryItems(existingRecord?.historyItems || []);
        setInitialUpdatePayload(
          buildUpdatePayloadFromValues(id, {
            ...nextRecord,
            attachments: nextRecord?.attachments || [],
          })
        );
      } catch (error) {
        if (!isMounted) return;
        setEstimationRecord(null);
        errorNotify(error || "Failed to fetch trip cost estimation detail");
      } finally {
        if (isMounted) {
          setPageLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      isMounted = false;
    };
  }, [id, locationEstimationRecord]);

  const handleFetchEstimationHistory = useCallback(
    async (historyType = "all") => {
      if (!canUseEstimationHistoryApi) return;

      setHistoryLoading(true);
      try {
        const response = await apiService.getTripCostEstimateHistory({
          trip_cost_estimate_id: estimationHistoryId,
          history_type: historyType,
        });
        const nextHistoryItems = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.results)
            ? response.data.results
            : Array.isArray(response?.results)
              ? response.results
              : [];

        setHistoryItems(nextHistoryItems);
        return nextHistoryItems;
      } catch (error) {
        errorNotify(error || "Failed to fetch trip cost estimation history");
        setHistoryItems([]);
        return [];
      } finally {
        setHistoryLoading(false);
      }
    },
    [canUseEstimationHistoryApi, estimationHistoryId]
  );

  const handleSubmitComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!canUseEstimationHistoryApi) return;

      setCommentLoading(true);
      try {
        await apiService.commentOnTripCostEstimate({
          comment,
          trip_cost_estimate_id: estimationHistoryId,
          parent_id,
          mention_user_ids,
        });
      } finally {
        setCommentLoading(false);
      }
    },
    [canUseEstimationHistoryApi, estimationHistoryId]
  );

  useEffect(() => {
    if (!canUseEstimationHistoryApi) {
      setHistoryItems([]);
      return;
    }

    handleFetchEstimationHistory("all");
  }, [canUseEstimationHistoryApi, handleFetchEstimationHistory]);

  if (pageLoading) {
    return <SpinnerLoader />;
  }

  if (!estimationRecord) {
    return (
      <div className="trip_cost_estimation_page">
        <Breadcrumbs values={["Finance", "Trip Cost Estimation", "Estimation Details"]} />
        <div className="add_trip_suggestion_div">
          <h4>Estimation not found</h4>
          <p>This trip cost estimation is not available right now.</p>
          <Button
            submitText="Back to Trip Cost Estimation"
            type="button"
            shouldRow={false}
            isShowCancel={false}
            submitBtn={() => navigate("/trip-cost-estimation")}
          />
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={estimationRecord}
      validationSchema={createTripCostEstimationSchema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const nextPayload = buildUpdatePayloadFromValues(id, values);
          const updatedKeys = buildUpdatedKeys(nextPayload, initialUpdatePayload || {});
          const finalPayload = {
            ...nextPayload,
            updatedKeys,
          };

          await apiService.updateTripCostEstimate(finalPayload);
          successNotify("Trip cost estimation details updated successfully");
          navigate("/trip-cost-estimation");
        } catch (error) {
          errorNotify(error || "Failed to update trip cost estimation");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        validateForm,
        handleSubmit,
        isSubmitting,
      }) => {
        const metrics = buildTripCostEstimationMetrics(values);
        const { tripOptions } = buildCurrentOptions(values, tripDropdownOptions);
        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function" ? updater(values.attachments || []) : updater;
          setFieldValue("attachments", nextFiles);
          setFieldTouched("attachments", true, false);
        };

        const handleTripSelection = async (option) => {
          const nextTripId = option?.value ?? "";
          const nextTripLabel = option?.label ?? "";

          setFieldValue("tripId", nextTripId, false);
          setFieldValue("tripRecordId", nextTripId, false);
          setFieldValue("tripLabel", nextTripLabel, false);
          setFieldValue("tripCode", nextTripLabel, false);
          setFieldTouched("tripId", false, false);
          resetFetchedTripFieldTouches(setFieldTouched);
          clearFetchedTripFields(setFieldValue);

          if (!nextTripId) {
            return;
          }

          setTripDetailLoading(true);

          try {
            const response = await apiService.getTripEstimateDetail(nextTripId);
            const detail = response?.data || {};
            const routeMeta = buildRouteMeta(detail);

            setFieldValue(
              "tripLabel",
              String(detail?.trip_id || nextTripLabel || nextTripId).trim(),
              false
            );
            setFieldValue(
              "tripCode",
              String(detail?.trip_id || nextTripLabel || nextTripId).trim(),
              false
            );
            setFieldValue(
              "vehicleId",
              String(
                detail?.vehicle?.registration_number ||
                  detail?.vehicle?.name ||
                  detail?.vehicle?.id ||
                  ""
              ).trim(),
              false
            );
            setFieldValue(
              "driverId",
              String(
                detail?.primary_driver?.id ||
                  detail?.primary_driver?.user_id ||
                  ""
              ).trim(),
              false
            );
            setFieldValue(
              "driverName",
              String(
                detail?.primary_driver?.username ||
                  detail?.primary_driver?.name ||
                  ""
              ).trim(),
              false
            );
            setFieldValue(
              "driverImage",
              String(detail?.primary_driver?.image || "").trim(),
              false
            );
            setFieldValue("routeId", String(routeMeta.routeId || "").trim(), false);
            setFieldValue(
              "routeLabel",
              String(routeMeta.routeLabel || "").trim(),
              false
            );
            setFieldValue("route", String(routeMeta.route || "").trim(), false);
            setFieldValue("distance", detail?.distance ?? "", false);
            setFieldValue(
              "tripDuration",
              String(detail?.duration || "").trim(),
              false
            );

            syncFetchedTripFieldValidation(setFieldTouched, validateForm, {
              ...values,
              tripId: nextTripId,
              tripRecordId: nextTripId,
              tripLabel: String(detail?.trip_id || nextTripLabel || nextTripId).trim(),
              tripCode: String(detail?.trip_id || nextTripLabel || nextTripId).trim(),
              vehicleId: String(
                detail?.vehicle?.registration_number ||
                  detail?.vehicle?.name ||
                  detail?.vehicle?.id ||
                  ""
              ).trim(),
              driverId: String(
                detail?.primary_driver?.id ||
                  detail?.primary_driver?.user_id ||
                  ""
              ).trim(),
              driverName: String(
                detail?.primary_driver?.username ||
                  detail?.primary_driver?.name ||
                  ""
              ).trim(),
              driverImage: String(detail?.primary_driver?.image || "").trim(),
              routeId: String(routeMeta.routeId || "").trim(),
              routeLabel: String(routeMeta.routeLabel || "").trim(),
              route: String(routeMeta.route || "").trim(),
              distance: detail?.distance ?? "",
              tripDuration: String(detail?.duration || "").trim(),
            });
          } catch (error) {
            clearFetchedTripFields(setFieldValue);
            syncFetchedTripFieldValidation(setFieldTouched, validateForm, {
              ...values,
              tripId: nextTripId,
              tripRecordId: nextTripId,
              tripLabel: nextTripLabel,
              tripCode: nextTripLabel,
              vehicleId: "",
              driverId: "",
              driverName: "",
              driverImage: "",
              routeId: "",
              routeLabel: "",
              route: "",
              distance: "",
              tripDuration: "",
            });
            errorNotify(error || "Failed to fetch trip estimate detail");
          } finally {
            setTripDetailLoading(false);
          }
        };

        return (
          <div className="trip_cost_estimation_page trip_cost_estimation_details_page">
            <Breadcrumbs values={["Finance", "Trip Cost Estimation", "Estimation Details"]} />

            <div className="page_header access_view">
              <h2>Estimation Details</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/trip-cost-estimation")}
                  submitBtn={handleSubmit}
                  submitText={isSubmitting ? "Saving..." : "Save Details"}
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={canUpdateEstimation}
                  loading={isSubmitting}
                  disabled={isSubmitting || tripDropdownLoading || tripDetailLoading}
                />
              </div>
            </div>

            <div className="page_container">
              <Row className="driver_page_layout trip_cost_estimation_details_layout">
                <Col lg={8}>
                  <div className="trip_cost_estimation_detail_bar">
                    <span>Estimation ID:</span>
                    <strong>{estimationRecord.estimationId}</strong>
                  </div>

                  <div className="trip_cost_estimation_profit_banner">
                    <span>Projected Profit (PKR)</span>
                    <strong>{formatNumber(metrics.projectedProfit)}</strong>
                  </div>

                  <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell">
                    <div className="driver_form_content">
                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Trip Overview</h3>
                          <p>Main information for this part record.</p>
                        </div>
                        <Row>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Trip ID")}
                              placeholder="Select Trip ID"
                              options={tripOptions}
                              loading={tripDropdownLoading}
                              value={pickOption(tripOptions, values.tripId)}
                              name="tripId"
                              onChange={handleTripSelection}
                              onBlur={handleBlur}
                              error={errors.tripId}
                              touched={touched.tripId}
                              isSearch
                              isDisabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Vehicle")}
                              type="text"
                              placeholder="Vehicle will auto-fill"
                              name="vehicleId"
                              value={values.vehicleId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.vehicleId}
                              touched={touched.vehicleId}
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Driver")}
                              type="text"
                              placeholder="Driver will auto-fill"
                              name="driverName"
                              value={values.driverName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.driverId}
                              touched={touched.driverId}
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Route")}
                              type="text"
                              placeholder="Route will auto-fill"
                              name="routeLabel"
                              value={values.routeLabel || values.route}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.routeId}
                              touched={touched.routeId}
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Distance")}
                              type="number"
                              placeholder="Distance will auto-fill"
                              name="distance"
                              value={values.distance}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.distance}
                              touched={touched.distance}
                              showExtraText
                              extraText="KM"
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Trip Duration")}
                              type="text"
                              placeholder="Trip duration will auto-fill"
                              name="tripDuration"
                              value={values.tripDuration}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.tripDuration}
                              touched={touched.tripDuration}
                              disabled
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Cost Estimation Inputs</h3>
                          <p>Main information for this part record.</p>
                        </div>
                        <Row>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Fuel Price per Liter")}
                              type="number"
                              placeholder="Enter Fuel Price"
                              name="fuelPricePerLiter"
                              value={values.fuelPricePerLiter}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.fuelPricePerLiter}
                              touched={touched.fuelPricePerLiter}
                              showExtraText
                              extraText="PKR"
                              disabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Vehicle Fuel Efficiency per Liter")}
                              type="number"
                              placeholder="Enter Fuel Efficiency"
                              name="vehicleFuelEfficiency"
                              value={values.vehicleFuelEfficiency}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.vehicleFuelEfficiency}
                              touched={touched.vehicleFuelEfficiency}
                              showExtraText
                              extraText="KM"
                              disabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Fuel Cost Estimate", false)}
                              type="number"
                              placeholder="Fuel Cost Estimate"
                              name="estimatedFuelCost"
                              value={metrics.estimatedFuelCost}
                              onChange={() => {}}
                              showExtraText
                              extraText="PKR"
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Maintenance Cost Estimate", false)}
                              type="number"
                              placeholder="Maintenance Cost Estimate"
                              name="estimatedMaintenanceCost"
                              value={metrics.estimatedMaintenanceCost}
                              onChange={() => {}}
                              showExtraText
                              extraText="PKR"
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Driver Allowance")}
                              type="number"
                              placeholder="Enter Driver Allowance"
                              name="foodAllowancePerDay"
                              value={values.foodAllowancePerDay}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.foodAllowancePerDay}
                              touched={touched.foodAllowancePerDay}
                              showExtraText
                              extraText="PKR"
                              disabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Estimated Toll Cost")}
                              type="number"
                              placeholder="Enter Toll Cost"
                              name="estimatedTollCost"
                              value={values.estimatedTollCost}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.estimatedTollCost}
                              touched={touched.estimatedTollCost}
                              showExtraText
                              extraText="PKR"
                              disabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Maintenance Cost per KM")}
                              type="number"
                              placeholder="Enter Maintenance Cost"
                              name="maintenanceCostPerKm"
                              value={values.maintenanceCostPerKm}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.maintenanceCostPerKm}
                              touched={touched.maintenanceCostPerKm}
                              showExtraText
                              extraText="PKR"
                              disabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Estimated Revenue")}
                              type="number"
                              placeholder="Enter Estimated Revenue"
                              name="estimatedRevenue"
                              value={values.estimatedRevenue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.estimatedRevenue}
                              touched={touched.estimatedRevenue}
                              showExtraText
                              extraText="PKR"
                              disabled={!canUpdateEstimation}
                            />
                          </Col>
                          <Col md={12}>
                            <div className="issue_file_upload_wrapper">
                              <label>Attachment</label>
                              <FileUpload
                                destination="trip-cost-estimation"
                                files={values.attachments || []}
                                setFiles={updateAttachments}
                                maxFileSize={10}
                                className="issue_file_upload create_vehicle_upload"
                                disabled={!canUpdateEstimation}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="service_cost_summary_details assets_vehicle_right_sidebar driver_details_sidebar trip_cost_estimation_details_sidebar">
                    <div className="service_cost_summary mb-3">
                      <h6>Cost Summary</h6>
                      <p>Financial Breakdown</p>

                      <div className="service_item">
                        <div>Maintenance (PKR)</div>
                        <div>{formatNumber(metrics.estimatedMaintenanceCost)}</div>
                      </div>
                      <div className="service_item">
                        <div>Fuel (PKR)</div>
                        <div>{formatNumber(metrics.estimatedFuelCost)}</div>
                      </div>
                      <div className="service_item">
                        <div>Driver Allowance (PKR)</div>
                        <div>{formatNumber(metrics.estimatedFoodCost)}</div>
                      </div>
                      <div className="service_item">
                        <div>Estimate Revenue (PKR)</div>
                        <div>{formatNumber(metrics.estimatedRevenue)}</div>
                      </div>

                      <div className="total">
                        <div>Projected Profit (PKR)</div>
                        <span>{formatNumber(metrics.projectedProfit)}</span>
                      </div>
                    </div>

                    <LogsComments
                      key={estimationHistoryId || id || "trip-cost-estimation-history"}
                      historyItems={historyItems}
                      commentValue={commentValue}
                      onCommentChange={setCommentValue}
                      onFetchHistory={handleFetchEstimationHistory}
                      onSubmitComment={handleSubmitComment}
                      loading={historyLoading || commentLoading}
                      editorModules={modules}
                      editorFormats={formats}
                      emptyMessages={{
                        all: "No comments or activity found",
                        comment: "No comments found",
                        logs: "No activity found",
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default TripCostEstimationDetails;
